/**
 * §H8 overlay — "layer A select (vector) · layer B select (vector or raster-mask presets:
 * erosion high, cropland) · operation (Intersect / Within distance 5 km / Erase / Union)";
 * result "RBAC pre-check both inputs (lock toast); summary table; `flagged units` count;
 * note that result is catalogued with lineage".
 *
 * **This card's lock is stricter than every other one's.** §H8 declares `minimum: FULL`
 * (T7), so a layer this reader may *view* is still not one they may overlay. The form
 * pre-checks `access === 'full'` on both inputs and refuses with the §I1 sentence rather
 * than posting a request that would come back 403 — the same "mirror of the server rule,
 * never the enforcement" §I2 asks for everywhere else.
 *
 * **`measure` is the word the share is of.** §H8 answers `share_a_pct` against whatever A
 * is measured in — a count of points, kilometres of river, km² of settlement — and printing
 * "% of layer A" over three different denominators is how a reader ends up comparing them.
 *
 * The two **presets** are keys, not layers: `erosion_high` and `cropland` are raster masks
 * §H8 resolves itself, and `inputs.b.preset` records the rule *and* the threshold it
 * resolved to, which is what the panel prints under the summary.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { aoiRing, useToolRun, type OverlayResult } from '../../../lib/analysis';
import { fmtKm2, fmtNum } from '../../../lib/format';
import { Icon } from '../../../lib/icons';
import { COUNTRY_NAMES, useAppStore } from '../../../lib/state';
import { toast } from '../../../lib/toast';
import type { ToolProps } from '../AnalysisDock';
import { ToolFrame } from '../ToolFrame';
import { ResultBox, ResultNote } from '../ResultBox';
import {
  analysisVectors,
  DEFAULT_OVERLAY_LAYER,
  firstAvailable,
  OVERLAY_PRESETS,
  useAnalysisLayers,
} from '../pickers';

/** `vector.OPS`, with the API's own token for "within distance". */
export const OPS = ['intersect', 'within_distance', 'erase', 'union'] as const;

export const DEFAULT_WITHIN_KM = 5;

export function Overlay({ onBack }: ToolProps) {
  const { t } = useTranslation();
  const country = useAppStore((s) => s.country);
  const scope = useAppStore((s) => s.scope);
  const aoi = useAppStore((s) => s.aoi);
  const addResultLayer = useAppStore((s) => s.addResultLayer);

  const layers = useAnalysisLayers();
  const vectors = useMemo(() => analysisVectors(layers), [layers]);
  const [layerA, setLayerA] = useState('');
  const [layerB, setLayerB] = useState<string>(OVERLAY_PRESETS[1]);
  const [op, setOp] = useState<(typeof OPS)[number]>('intersect');
  const [distance, setDistance] = useState(DEFAULT_WITHIN_KM);
  const [extent, setExtent] = useState<'scope' | 'aoi'>('scope');
  const run = useToolRun<OverlayResult>('overlay');

  const a = firstAvailable(vectors, layerA, DEFAULT_OVERLAY_LAYER);
  const isPreset = (OVERLAY_PRESETS as readonly string[]).includes(layerB);

  const scopeLabel = scope
    ? scope.name
    : country === 'ALL'
      ? t('analysis.change.programme')
      : t(`country.${country}`, COUNTRY_NAMES[country]);

  /** §I3/§I1: §H8 needs FULL on every named input, so the form says so before the POST. */
  const blocked = [a, isPreset ? null : layerB]
    .filter((id): id is string => Boolean(id))
    .map((id) => layers.find((l) => l.id === id))
    .filter((l) => l && l.access !== 'full');

  const submit = () => {
    if (!a || !layerB) {
      toast(t('analysis.noLayer'));
      return;
    }
    if (blocked.length) {
      toast(t('analysis.overlay.locked', { layer: blocked[0]?.name ?? '' }));
      return;
    }
    run.run({
      layer_a: a,
      layer_b: layerB,
      op,
      ...(op === 'within_distance' ? { distance_km: distance } : {}),
      ...(extent === 'aoi' && aoi
        ? { aoi_polygon: aoiRing(aoi.pts) }
        : scope
          ? { unit_id: scope.unitId }
          : country === 'ALL'
            ? {}
            : { country }),
    });
  };

  const result = run.result;

  const addToMap = () => {
    const spec = result?.result_layers?.[0];
    if (!spec) return;
    addResultLayer(spec, { jobId: run.jobId ?? spec.id, tool: 'overlay' });
    toast(t('analysis.overlay.added'));
  };

  const measureValue = (r: OverlayResult): string => {
    if (r.measure === 'length_km') return `${fmtNum(r.overlap_km, 1)} km`;
    if (r.measure === 'area_km2') return fmtKm2(r.overlap_km2, 1);
    return fmtNum(r.n_features, 0);
  };

  return (
    <ToolFrame
      tool="overlay"
      onBack={onBack}
      phase={run.phase}
      progress={run.progress}
      error={run.error}
      cached={run.cached}
      result={
        result ? (
          <>
            <ResultBox
              title={t('analysis.overlay.title', {
                a: String((result.inputs.a as { name?: string }).name ?? ''),
                b: String((result.inputs.b as { name?: string }).name ?? ''),
              })}
            >
              <table className="mini">
                <tbody>
                  <tr>
                    <td>{t('analysis.overlay.features')}</td>
                    <td className="num">{fmtNum(result.n_features, 0)}</td>
                  </tr>
                  <tr>
                    <td>{t(`analysis.overlay.measure.${result.measure}`)}</td>
                    <td className="num">{measureValue(result)}</td>
                  </tr>
                  <tr>
                    <td>
                      {t('analysis.overlay.shareA', {
                        measure: t(`analysis.overlay.measureWord.${result.measure}`),
                      })}
                    </td>
                    <td className="num">
                      {result.share_a_pct === null ? '—' : `${fmtNum(result.share_a_pct, 1)}%`}
                    </td>
                  </tr>
                  <tr>
                    <td>{t('analysis.overlay.flagged')}</td>
                    <td className="num">{fmtNum(result.n_flagged, 0)}</td>
                  </tr>
                </tbody>
              </table>
            </ResultBox>

            <ResultNote>
              {t('analysis.overlay.lineage', {
                id: result.derived_dataset_id,
                rows: fmtNum(result.derived.rows, 0),
                extent: result.extent.label,
              })}
            </ResultNote>
            {(result.notes ?? []).map((note) => (
              <ResultNote key={note}>{t(`analysis.note.${note}`, note)}</ResultNote>
            ))}

            {result.result_layers.length ? (
              <div className="ad-actions">
                <button type="button" className="btn primary sm" id="ovMap" onClick={addToMap}>
                  <Icon name="map" size={12} /> {t('analysis.overlay.show')}
                </button>
              </div>
            ) : null}
          </>
        ) : null
      }
    >
      <label className="fld" htmlFor="ovA">
        {t('analysis.overlay.layerA')}
      </label>
      <select className="std" id="ovA" value={a} onChange={(e) => setLayerA(e.target.value)}>
        {vectors.map((l) => (
          <option key={l.id} value={l.id}>
            {l.name}
          </option>
        ))}
        {vectors.length === 0 ? <option value="">{t('analysis.noLayerOption')}</option> : null}
      </select>

      <label className="fld" htmlFor="ovB">
        {t('analysis.overlay.layerB')}
      </label>
      <select className="std" id="ovB" value={layerB} onChange={(e) => setLayerB(e.target.value)}>
        <optgroup label={t('analysis.overlay.presets')}>
          {OVERLAY_PRESETS.map((key) => (
            <option key={key} value={key}>
              {t(`analysis.overlay.preset.${key}`)}
            </option>
          ))}
        </optgroup>
        <optgroup label={t('analysis.overlay.vectors')}>
          {vectors.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name}
            </option>
          ))}
        </optgroup>
      </select>

      <label className="fld" htmlFor="ovOp">
        {t('analysis.overlay.operation')}
      </label>
      <select
        className="std"
        id="ovOp"
        value={op}
        onChange={(e) => setOp(e.target.value as (typeof OPS)[number])}
      >
        {OPS.map((key) => (
          <option key={key} value={key}>
            {t(`analysis.overlay.op.${key}`)}
          </option>
        ))}
      </select>

      {op === 'within_distance' ? (
        <>
          <label className="fld" htmlFor="ovDist">
            {t('analysis.overlay.distance')}
          </label>
          <input
            type="number"
            className="std"
            id="ovDist"
            min={0.1}
            max={100}
            step={0.1}
            value={distance}
            onChange={(e) => setDistance(Number(e.target.value))}
          />
        </>
      ) : null}

      <label className="fld" htmlFor="ovExt">
        {t('analysis.change.extent')}
      </label>
      <select
        className="std"
        id="ovExt"
        value={extent}
        onChange={(e) => setExtent(e.target.value as 'scope' | 'aoi')}
      >
        <option value="scope">{scopeLabel}</option>
        {aoi ? <option value="aoi">{t('analysis.aoiName')}</option> : null}
      </select>

      {blocked.length ? (
        <div className="note" data-role="locked">
          <Icon name="lock" size={12} /> {t('analysis.overlay.locked', { layer: blocked[0]?.name ?? '' })}
        </div>
      ) : null}

      <button
        type="button"
        className="btn primary ad-run"
        id="ovRun"
        disabled={run.phase === 'running' || blocked.length > 0}
        onClick={submit}
      >
        <Icon name="play" size={13} /> {t('analysis.overlay.run')}
      </button>
    </ToolFrame>
  );
}

export default Overlay;
